import { useMemo } from 'react'
import { generatePath, useLocation, useMatches, useNavigate, useParams } from 'react-router'
import { routesByName, type RouteMeta } from './routes'

/**
 * Vue-Router-shaped navigation on top of React Router, so ported code can
 * keep `router.push({ name: 'CityHome', params: { cityId } })`.
 */

export type QueryValue = string | number | boolean | null | undefined
export type Query = Record<string, QueryValue | QueryValue[]>

export type RouteLocation =
  | string
  | {
      name?: string
      path?: string
      params?: Record<string, string | number | undefined>
      query?: Query
      hash?: string
      replace?: boolean
    }

export function buildQueryString (query?: Query): string {
  if (!query) return ''
  const sp = new URLSearchParams()
  for (const [key, raw] of Object.entries(query)) {
    const values = Array.isArray(raw) ? raw : [raw]
    for (const v of values) {
      if (v === undefined) continue
      sp.append(key, v === null ? '' : String(v))
    }
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/** Resolve a location (string, named route, or path + query) to a URL string. */
export function resolveLocation (to: RouteLocation): string {
  if (typeof to === 'string') return to
  let path = to.path || '/'
  if (to.name) {
    const route = routesByName.get(to.name)
    if (!route) throw new Error(`Unknown route name: ${to.name}`)
    const params: Record<string, string> = {}
    for (const [k, v] of Object.entries(to.params || {})) {
      if (v !== undefined) params[k] = String(v)
    }
    path = generatePath(route.path, params)
  }
  const hash = to.hash ? (to.hash.startsWith('#') ? to.hash : `#${to.hash}`) : ''
  return `${path}${buildQueryString(to.query)}${hash}`
}

/**
 * <ScrollRestoration> scrolls to the top on every new history entry. A
 * query-only change (filters, clearing a payment-return flag) keeps the same
 * page mounted, so it keeps the reader's place too.
 */
function navigateOptions (href: string, replace: boolean) {
  const preventScrollReset = new URL(href, window.location.href).pathname === window.location.pathname
  return { replace, preventScrollReset }
}

/** `router.push / replace / back` with Vue Router's call shape. */
export function useAppRouter () {
  const navigate = useNavigate()
  return useMemo(
    () => ({
      push: (to: RouteLocation) => {
        const replace = typeof to === 'object' && to.replace === true
        const href = resolveLocation(to)
        return Promise.resolve(navigate(href, navigateOptions(href, replace)))
      },
      replace: (to: RouteLocation) => {
        const href = resolveLocation(to)
        return Promise.resolve(navigate(href, navigateOptions(href, true)))
      },
      back: () => navigate(-1),
      go: (delta: number) => navigate(delta),
      resolve: (to: RouteLocation) => ({ href: resolveLocation(to) })
    }),
    [navigate]
  )
}

export interface CurrentRoute {
  name: string | undefined
  path: string
  fullPath: string
  hash: string
  params: Record<string, string>
  /** First value for each key, like Vue Router's `route.query` for single values. */
  query: Record<string, string>
  meta: RouteMeta
}

export interface RouteHandle {
  name?: string
  meta?: RouteMeta
}

/** Vue-Router-shaped `useRoute()`. */
export function useRoute (): CurrentRoute {
  const location = useLocation()
  const params = useParams()
  const matches = useMatches()
  return useMemo(() => {
    const leaf = matches[matches.length - 1]
    const handle = (leaf?.handle || {}) as RouteHandle
    const query: Record<string, string> = {}
    new URLSearchParams(location.search).forEach((v, k) => {
      if (!(k in query)) query[k] = v
    })
    const cleanParams: Record<string, string> = {}
    for (const [k, v] of Object.entries(params)) if (v !== undefined) cleanParams[k] = v
    return {
      name: handle.name,
      path: location.pathname,
      fullPath: `${location.pathname}${location.search}${location.hash}`,
      hash: location.hash,
      params: cleanParams,
      query,
      meta: handle.meta || {}
    }
  }, [location, params, matches])
}
